<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('serviceRequest.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('serviceRequest.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-flex ts-space-x-3">
          <ts-button outline color="info" @click.prevent="addNew">
            {{ $t('add_new') }}
          </ts-button>
          <ts-button outline @click.prevent="goToGenerateInv">
            {{ $t('serviceRequest.generateInvoice') }}
          </ts-button>
          <a-range-picker
            v-model="dateRange"
            :allowClear="true"
            format="DD-MM-YYYY"
            valueFormat="DD-MM-YYYY"
            style="width: 200px"
            class="date-range"
            @change="fetchData()"
          />
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table-filter-v2
          :columns="columns"
          :data="resources"
          :loading="loading"
          @search="search"
        >
          <template slot-scope="{ row }" slot="branch">
            {{ row.branch ? row.branch.branch_name_en : '' }}
          </template>
          <template slot-scope="{ row }" slot="service_request_type">
            <a-tooltip placement="topLeft">
              <template slot="title">
                <span>
                  {{ $t('serviceRequest.remarks') }}:
                  {{ row.remarks ? row.remarks : 'N/A' }}
                </span>
              </template>
              <span class="ts-cursor-pointer">
                {{ row.service_request_type }}
              </span>
            </a-tooltip>
          </template>
          <template slot-scope="{ row }" slot="fee_type">
            {{ row.fee_type ? row.fee_type.product_name_en : '' }}
          </template>
          <template slot-scope="{ row }" slot="pro_group">
            {{ row.product ? row.product.pro_group_name_en : '' }}
          </template>
          <template slot-scope="{ row }" slot="product">
            {{ row.product ? row.product.product_name_en : '' }}
          </template>
          <template slot-scope="{ row }" slot="serial_no">
            {{ row.product_serial ? row.product_serial.imei : '' }}
          </template>
          <template slot-scope="{ row }" slot="requested_by">
            {{ row.customer ? row.customer.customer_name : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="contact_number">
            {{ row.customer ? row.customer.contact_number : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="village_name">
            {{ row.customer ? row.customer.village_name : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="commune_name">
            {{ row.customer ? row.customer.commune_name : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="district_name">
            {{ row.customer ? row.customer.district_name : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="province_name">
            {{ row.customer ? row.customer.province_name : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="informant">
            {{ row.informant_by ? row.informant_by.employee_name_en : 'N/A' }}
          </template>
          <template slot-scope="{ row }" slot="owner">
            <a-select
              v-model="row.owner"
              class="ts-w-full"
              size="small"
              placeholder="Select"
              show-search
              option-filter-prop="children"
              :loading="row._deleting"
              :disabled="row._deleting || disabledUpdated(row)"
              :options="owners"
              @change="onChangeOwner(row)"
            >
            </a-select>
          </template>
          <template slot-scope="{ row }" slot="technician1">
            {{
              row.technician1_by ? row.technician1_by.employee_name_en : 'N/A'
            }}
          </template>
          <template slot-scope="{ row }" slot="technician2">
            {{
              row.technician2_by ? row.technician2_by.employee_name_en : 'N/A'
            }}
          </template>
          <template slot-scope="{ row }" slot="service_status">
            <a-select
              v-model="row.service_status"
              class="ts-w-full"
              size="small"
              placeholder="Select"
              :loading="row._deleting"
              :disabled="row._deleting || disabledUpdated(row)"
              @change="onChangeServiceStatus(row)"
            >
              <a-select-option
                v-for="item in serviceStatus"
                :key="item.value"
                :value="item.value"
                class="ts-items-center"
              >
                <a-icon
                  type="tag"
                  theme="filled"
                  :style="'color:' + item.color + ';'"
                />
                {{ item.label }}
              </a-select-option>
            </a-select>
          </template>
          <template slot-scope="{ row }" slot="generated_invoice">
            <span
              v-if="row.payment_invoices.length > 0"
              class="badge bg-primary tw-capitalize px-2"
              >Yes
            </span>
            <span v-else class="badge bg-danger tw-capitalize px-2">No </span>
          </template>
          <template slot-scope="{ row }" slot="actual_duration">
            <span
              class="badge px-2 fs-11px d-inline-flex"
              :class="getBgForDuration(row)"
            >
              {{ getActualDuration(row) }}
            </span>
          </template>
          <template slot-scope="{ row }" slot="required_spare_part">
            <i-switch
              size="small"
              v-model="row.required_spare_part"
              :loading="row._deleting"
              :disabled="row._deleting || disabledUpdated(row)"
              @on-change="onChangeRequiredSparePart(row)"
            >
            </i-switch>
          </template>
          <template slot-scope="{ row }" slot="action">
            <div
              class="ts-flex ts-justify-end ts-space-x-2"
              style="width: fit-content;"
            >
              <a
                href="#"
                v-tooltip="$t('serviceRequest.viewDetail')"
                class="text-success"
                @click.prevent="viewDetail(row)"
              >
                <i class="fas fa-eye"></i>
              </a>
              <a
                v-if="!(!row.required_spare_part || disabledUpdated(row))"
                href="#"
                v-tooltip="$t('serviceRequest.createSparePart')"
                class="text-success"
                @click.prevent="createSparePart(row)"
              >
                <i class="fas fa-wrench"></i>
              </a>
              <a
                v-if="!disabledUpdated(row)"
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(row)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                v-if="!disabledUpdated(row)"
                placement="leftTop"
                v-tooltip="$t('delete')"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(row)"
              >
                <a href="#" :disabled="row._deleting">
                  <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </div>
          </template>
        </ts-table-filter-v2>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
      <a-modal
        v-if="showModal"
        v-model="showModal"
        :title="$t('serviceRequest.pageTitle')"
        :footer="null"
        :centered="true"
        :maskClosable="false"
        :zIndex="1000"
        width="50%"
        @cancel="onCancel"
      >
        <Form @cancel="onCancel" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import Form from './form.vue'
import moment from 'moment'

export default {
  name: 'serviceRequest',
  components: {
    Form
  },
  data() {
    return {
      showModal: false,
      loading: false,
      showForm: false,
      branch_id: []
    }
  },

  created() {
    this.getPreData({
      params: {},
      index: undefined
    })
  },
  computed: {
    ...mapState('serviceManagement/serviceRequest', [
      'resources',
      'pagination',
      'formViewDatas'
    ]),
    dateRange: {
      get() {
        return this.$store.state.serviceManagement.serviceRequest.dateRange
      },
      set(newValue) {
        this.$store.commit(
          'serviceManagement/serviceRequest/SET_DATE_RANGE',
          newValue
        )
      }
    },
    owners() {
      if (this.formViewDatas['owners']) {
        return this.formViewDatas['owners'].map(el => ({
          value: el.user_id,
          label: el.employee_name_en
        }))
      }
      return []
    },
    serviceRequestTypes() {
      if (this.formViewDatas['serviceRequestTypes']) {
        return this.formViewDatas['serviceRequestTypes'].map(el => ({
          value: el.service_request_type_id,
          label: el.service_request_type,
          service_request_schedule: el.service_request_schedule
        }))
      }
      return []
    },
    feeTypes() {
      if (this.formViewDatas['feeTypes']) {
        return this.formViewDatas['feeTypes'].map(el => ({
          value: el.product_id,
          label: el.product_name_en
        }))
      }
      return []
    },
    serviceStatus() {
      return [
        {
          label: 'Pending',
          value: 0,
          color: '#6c757d'
        },
        {
          label: 'Progressing',
          value: 1,
          color: '#17a2b8'
        },
        {
          label: 'Completed',
          value: 2,
          color: '#28a745'
        },
        {
          label: 'Cancel',
          value: 3,
          color: '#f59c1a'
        },
        {
          label: 'Closed',
          value: 4,
          color: '#dc3545'
        }
      ]
    },
    columns() {
      return [
        {
          title: this.$t('serviceRequest.branch'),
          key: 'branch_name_en',
          width: 100
        },
        {
          title: this.$t('serviceRequest.serviceRequestDate'),
          key: 'service_request_date',
          width: 80
        },
        {
          title: this.$t('serviceRequest.serviceRequestNo'),
          key: 'service_request_no',
          keySearch: 'service_request_no',
          search: true,
          sortable: true,
          sortKey: 'service_request_no',
          width: 80
        },
        {
          title: this.$t('serviceRequest.orderKads'),
          key: 'order_kads',
          keySearch: 'order_kads',
          search: true,
          width: 80
        },
        {
          title: this.$t('serviceRequest.serviceRequestType'),
          slot: 'service_request_type',
          keySearch: 'service_request_mains.service_request_type_id',
          search: true,
          sortable: true,
          sortKey: 'service_request_type_id',
          filters: this.serviceRequestTypes,
          width: 150,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.feeType'),
          key: 'fee_type',
          keySearch: 'fee_type_id',
          search: true,
          sortable: true,
          sortKey: 'fee_type_id',
          filters: this.feeTypes,
          width: 125,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.proGroup'),
          key: 'pro_group_name_en',
          keySearch: 'pro_group_name_en',
          search: true,
          width: 150,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.product'),
          key: 'product_name_en',
          keySearch: 'pro.product_name_en',
          search: true,
          width: 150,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.serialNo'),
          key: 'imei',
          keySearch: 'service_request_mains.imei',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.requestedBy'),
          key: 'customer_name',
          keySearch: 'cus.customer_name',
          search: true,
          width: 150,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.phoneNo'),
          key: 'cus_contact_number',
          keySearch: 'cus.contact_number',
          search: true,
          width: 150,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.village'),
          key: 'cus_village_name',
          keySearch: 'cusv.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.commune'),
          key: 'cus_commune_name',
          keySearch: 'cusc.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.district'),
          key: 'cus_district_name',
          keySearch: 'cusd.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.province'),
          key: 'cus_province_name',
          keySearch: 'cusp.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.informant'),
          key: 'informant_by',
          keySearch: 'informant_emp.employee_name_en',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.invoiceDate'),
          key: 'stock_out_date',
          keySearch: 'invoice.stock_out_date',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.owner'),
          slot: 'owner',
          keySearch: 'owner_emp.employee_name_en',
          search: true,
          width: 110
        },
        {
          title: this.$t('serviceRequest.contact'),
          key: 'contact_number',
          keySearch: 'service_request_mains.contact_number',
          search: true,
          width: 150,
          class: 'ts-whitespace-no-wrap'
        },
        {
          title: this.$t('serviceRequest.serviceVillage'),
          key: 'village_name',
          keySearch: 'v.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.serviceCommune'),
          key: 'commune_name',
          keySearch: 'c.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.serviceDistrict'),
          key: 'district_name',
          keySearch: 'd.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.serviceProvince'),
          key: 'province_name',
          keySearch: 'p.name_kh',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.plateNumber'),
          key: 'plate_number',
          keySearch: 'plate_number',
          search: true,
          width: 80
        },
        {
          title: this.$t('serviceRequest.technician1'),
          key: 'technician1_by',
          keySearch: 'technician1_emp.employee_name_en',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.technician2'),
          key: 'technician2_by',
          keySearch: 'technician2_emp.employee_name_en',
          search: true,
          width: 150
        },
        {
          title: this.$t('serviceRequest.serviceStatus'),
          slot: 'service_status',
          keySearch: 'service_status',
          search: true,
          filters: this.serviceStatus,
          width: 150
        },
        {
          title: this.$t('serviceRequest.plannedStartDate'),
          key: 'planned_start_date',
          width: 80
        },
        {
          title: this.$t('serviceRequest.plannedFinishDate'),
          key: 'planned_finish_date',
          width: 80
        },
        {
          title: this.$t('serviceRequest.actualDuration'),
          slot: 'actual_duration',
          width: 120,
          class: 'ts-text-center'
        },
        {
          title: this.$t('serviceRequest.requiredSparePart'),
          slot: 'required_spare_part',
          width: 100,
          class: 'ts-text-center'
        },
        {
          title: this.$t('serviceRequest.generatedInvoice'),
          slot: 'generated_invoice',
          width: 100,
          class: 'ts-text-center'
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          searchIcon: true,
          width: 100,
          class: 'ts-right-0 ts-sticky ts-z-10 ts-bg-gray-100'
        }
      ]
    }
  },
  methods: {
    getBgForDuration(row) {
      if (row.planned_finish_date && row.close_date) {
        const end_date = moment(row.close_date, 'DD-MM-YYYY HH:mm:ss')
        const planned_finish_date = moment(
          row.planned_finish_date,
          'DD-MM-YYYY HH:mm:ss'
        )

        if (end_date.isAfter(planned_finish_date)) {
          return 'bg-warning'
        } else {
          return 'bg-success'
        }
      }
      return ''
    },
    getActualDuration(row) {
      if (row.open_date && row.close_date) {
        const open_date = moment(row.open_date, 'DD-MM-YYYY hh:mm:ss A')
        const close_date = moment(row.close_date, 'DD-MM-YYYY hh:mm:ss A')

        const diff = moment.duration(close_date.diff(open_date))

        return `${diff.hours()}h ${diff.minutes()}mn`
      }
      return 'N/A'
    },
    search(newValue) {
      this.$store.commit(
        'serviceManagement/serviceRequest/SET_SEARCH',
        newValue
      )
      this.$store.commit('serviceManagement/serviceRequest/RESET_CURRENT_PAGE')
      this.fetchData()
    },
    async getPreData({ params, index }) {
      this.loading = true
      await this.$store.dispatch(
        'serviceManagement/serviceRequest/getFormViewModel',
        { index: index, params: params }
      )
      this.loading = false
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('serviceManagement/serviceRequest/fetch', {
          ...attributes
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    addNew() {
      this.$router.push({ name: 'service-request-create' })
    },
    goToGenerateInv() {
      this.$router.push({ name: 'service-request-generate' })
    },
    onCancel() {
      this.showModal = false
      this.fetchData()
    },
    createSparePart(record) {
      this.$router.push({
        name: 'borrowing-spare-part-create-service-request',
        params: { service_request_id: record.service_request_id }
      })
    },
    viewDetail(record) {
      this.$router.push({
        name: 'service-request-view',
        params: {
          id: record.service_request_id
        }
      })
    },
    edit(record) {
      this.$router.push({
        name: 'service-request-edit',
        params: { id: record.service_request_id }
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch(
          'serviceManagement/serviceRequest/destroy',
          record.service_request_id
        )
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    disabledUpdated(record) {
      const disabled =
        (record.payment_invoices.length > 0 ? true : false) ||
        (record.service_status == 4 ? true : false)
      return disabled
    },
    onChangeOwner(record) {
      record._deleting = true
      this.$store
        .dispatch('serviceManagement/serviceRequest/changeOwner', {
          id: record.service_request_id,
          data: { owner: record.owner }
        })
        .then(response => {
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.fetchData()
        })
        .finally(() => (record._deleting = false))
    },
    onChangeServiceStatus(record) {
      record._deleting = true
      this.$store
        .dispatch('serviceManagement/serviceRequest/changeServiceStatus', {
          id: record.service_request_id,
          data: { service_status: record.service_status }
        })
        .then(response => {
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.fetchData()
        })
        .finally(() => (record._deleting = false))
    },
    async onChangeRequiredSparePart(record) {
      record._deleting = true
      this.$store
        .dispatch('serviceManagement/serviceRequest/changeRequiredSparePart', {
          id: record.service_request_id,
          data: { required_spare_part: record.required_spare_part }
        })
        .then(response => {
          this.$toasted.success(response.message)
        })
        .catch(error => {
          record.required_spare_part = !record.required_spare_part
          this.$toasted.error(error.message)
        })
        .finally(() => (record._deleting = false))
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500),
    branch_id: debounce(function(value) {
      this.fetchData({ branch_id: value })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('serviceManagement/serviceRequest/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
<style>
.anticon {
  vertical-align: 0;
}

.date-range .ant-calendar-picker-input {
  height: 34px !important;
}
</style>
